/**
 * Exportação do Painel Comercial — o que está na tela (ADR-0079).
 *
 * Antes o Exportar sempre levava os mesmos 8 indicadores + ranking: ignorava
 * os cards adicionados, incluía os ocultos e não dizia a régua de cada número.
 * Agora exporta os cards VISÍVEIS, na ordem da tela, cada um com a régua da
 * explicação ("?"). Indicadores viram linhas de uma tabela; faixas viram uma
 * seção própria.
 *
 * `EXPORTADORES[id](d, ctx)` devolve:
 *   { tipo: "kpi", valor, unidade, variacao }   ou
 *   { tipo: "faixa", columns, rows }
 */

const kpi = (valor, unidade, variacao = null) => ({
  tipo: "kpi",
  valor: valor ?? null,
  unidade,
  variacao: variacao ?? null,
});

const faixa = (columns, rows) => ({ tipo: "faixa", columns, rows });

const num = (v) => (v === null || v === undefined || v === "" ? null : Number(v));

export const EXPORTADORES = {
  pipeline: (d) => kpi(num(d?.pipeline?.soma), "R$"),
  receita_realizada: (d, { ex, cab }) =>
    kpi(num(cab?.receita_realizada ?? ex?.receita_realizada?.valor), "R$",
      num(ex?.receita_realizada?.delta_pct)),
  taxa_conversao: (d, { ex }) =>
    kpi(num(d?.taxa_conversao?.taxa), "%", num(ex?.taxa_conversao?.delta_pt)),
  ticket_medio: (d, { ex }) =>
    kpi(num(d?.ticket_medio?.media), "R$", num(ex?.ticket_medio?.delta_pct)),
  tempo_resposta: (d, { ex }) =>
    kpi(num(ex?.tempo_resposta?.horas), "h", num(ex?.tempo_resposta?.delta_horas)),
  tempo_medio_aprovacao: (d, { ex }) =>
    kpi(num(d?.tempo_medio_aprovacao?.dias), "dias", num(ex?.lead_time?.delta_dias)),
  margem_media: (d, { ex }) =>
    kpi(num(ex?.margem_media?.pct), "%", num(ex?.margem_media?.delta_pt)),
  aprovacoes_fora_limite: (d, { ex }) =>
    kpi(num(ex?.aprovacoes_fora_limite?.qtd), "qtd", num(ex?.aprovacoes_fora_limite?.delta_n)),
  valor_ponderado: (d) => kpi(num(d?.valor_ponderado?.soma), "R$"),
  comissao_prevista: (d) => kpi(num(d?.comissao_prevista_total), "R$"),
  leads_sem_orcamento: (d) => kpi(num(d?.leads_sem_orcamento?.qtd), "qtd"),
  valor_perdido: (d) => kpi(num(d?.perdas?.valor_perdido), "R$"),
  taxa_perda: (d) => kpi(num(d?.perdas?.taxa_perda), "%"),

  funil: (d, { funil }) =>
    faixa(
      [
        { key: "etapa", label: "Etapa", type: "text" },
        { key: "quantidade", label: "Quantidade", type: "number" },
        { key: "pct", label: "% do total", type: "number" },
      ],
      (funil?.niveis || []).map((n) => ({
        etapa: n.label,
        quantidade: num(n.count),
        pct: num(n.pct),
      }))
    ),
  valor_por_etapa: (d) =>
    faixa(
      [
        { key: "etapa", label: "Etapa", type: "text" },
        { key: "leads", label: "Leads", type: "number" },
        { key: "valor", label: "Valor", type: "currency" },
      ],
      (d?.valor_por_etapa || []).map((e) => ({
        etapa: e.etapa_nome,
        leads: num(e.qtd),
        valor: num(e.valor),
      }))
    ),
  por_que_perdemos: (d, { perdas }) =>
    faixa(
      [
        { key: "motivo", label: "Motivo", type: "text" },
        { key: "quantidade", label: "Quantidade", type: "number" },
        { key: "pct", label: "% dos perdidos", type: "number" },
      ],
      (perdas || []).map((r) => ({
        motivo: r.label,
        quantidade: num(r.count),
        pct: num(r.pct),
      }))
    ),
  motivos_perda: (d) =>
    faixa(
      [
        { key: "motivo", label: "Motivo", type: "text" },
        { key: "quantidade", label: "Quantidade", type: "number" },
        { key: "valor", label: "Valor perdido", type: "currency" },
      ],
      (d?.perdas?.motivos || []).map((m) => ({
        motivo: m.label,
        quantidade: num(m.count),
        valor: num(m.valor),
      }))
    ),
  ranking_vendedores: (d, { ranking }) =>
    faixa(
      [
        { key: "posicao", label: "Pos.", type: "number" },
        { key: "vendedor", label: "Vendedor", type: "text" },
        { key: "deals", label: "Deals", type: "number" },
        { key: "conversao", label: "Conversão (%)", type: "number" },
        { key: "valor_ganho", label: "Valor ganho", type: "currency" },
        { key: "delta", label: "Variação (%)", type: "number" },
      ],
      (ranking || []).map((r, i) => ({
        posicao: r.posicao ?? i + 1,
        vendedor: r.nome,
        deals: num(r.ganhos),
        conversao: num(r.taxa_conversao),
        valor_ganho: num(r.valor_ganho),
        delta: num(r.delta_pct),
      }))
    ),
};

// Monta as seções do arquivo a partir dos cards visíveis, na ordem da tela.
// `rotulo(id)` = título do card; `regua(id)` = a régua da explicação.
export function montarSecoesExportacao({ visiveis, d, ctx, rotulo, regua }) {
  const linhasKpi = [];
  const faixas = [];
  visiveis.forEach((id) => {
    const exportar = EXPORTADORES[id];
    if (!exportar) return;
    const saida = exportar(d, ctx);
    if (saida.tipo === "kpi") {
      linhasKpi.push({
        indicador: rotulo(id),
        valor: saida.valor,
        unidade: saida.unidade,
        variacao: saida.variacao,
        regua: regua(id) || "",
      });
    } else {
      faixas.push({
        id,
        label: regua(id) ? `${rotulo(id)} — ${regua(id)}` : rotulo(id),
        columns: saida.columns,
        rows: saida.rows,
      });
    }
  });
  const secoes = [];
  if (linhasKpi.length) {
    secoes.push({
      id: "indicadores",
      label: "Indicadores",
      columns: [
        { key: "indicador", label: "Indicador", type: "text" },
        { key: "valor", label: "Valor", type: "number" },
        { key: "unidade", label: "Unidade", type: "text" },
        { key: "variacao", label: "Variação", type: "number" },
        { key: "regua", label: "Como é contado", type: "text" },
      ],
      rows: linhasKpi,
    });
  }
  return [...secoes, ...faixas];
}
